import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  writeBatch,
  runTransaction,
  limit,
  query,
} from "firebase/firestore";
import {
  resolveConnectionRuntimeConfig,
  hasLiveCredentials,
} from "../runtime/connectionRuntimeConfigService";
import { checkAuthentication, runCollectionTests, validateConfiguration, checkStorage } from "./connectionVerifyEngine";
import { saveConnectionDeepTestHistory } from "./connectionHistoryService";
import {
  FIREBASE_SDK_VERSION,
  detectBrowser,
  detectOs,
  nowIso,
} from "./connectionVerifyUtils";
import {
  classifyFirestoreTestError,
  cleanupHealthTestDocuments,
  healthTestCollectionName,
} from "./healthTestCollectionUtils";
import {
  logFirestoreOperationResult,
  resolveConnectionTestRuntime,
} from "./connectionTestRuntimeService";
import {
  HEALTH_CHECK_MODE_CONNECTION_ONLY,
  WRITE_TESTS_SKIPPED_MESSAGE,
} from "./firestoreTestMessages";

/** Every temporary document lives inside this sandbox collection only. */
export const DEEP_TEST_COLLECTION = healthTestCollectionName();
/** The primary temporary document created by the write test. */
export const DEEP_TEST_DOC_ID = "connectionTest";
/** Number of documents written by the batch test. */
export const DEEP_TEST_BATCH_SIZE = 10;

/**
 * Ordered definition of the 17 test steps plus their live-progress percentage.
 * The percentages match the Sprint 10.1 UI specification.
 */
export const DEEP_TEST_STEPS = [
  { id: "configuration", label: "Configuration Validation", progress: 10 },
  { id: "authentication", label: "Authentication Test", progress: 20 },
  { id: "storage", label: "Storage Test", progress: 25 },
  { id: "read", label: "Firestore Read Test", progress: 30 },
  { id: "write", label: "Firestore Write Test", progress: 40 },
  { id: "readback", label: "Firestore Read Back", progress: 45 },
  { id: "update", label: "Update Test", progress: 50 },
  { id: "transaction", label: "Transaction Test", progress: 60 },
  { id: "batchWrite", label: "Batch Write Test", progress: 70 },
  { id: "batchDelete", label: "Batch Delete Test", progress: 75 },
  { id: "delete", label: "Delete Test", progress: 80 },
  { id: "rollback", label: "Rollback Verification", progress: 90 },
  { id: "permissions", label: "Permission Test", progress: 92 },
  { id: "collections", label: "Collection Discovery", progress: 94 },
  { id: "database", label: "Database Information", progress: 96 },
  { id: "performance", label: "Performance", progress: 97 },
  { id: "warnings", label: "Warnings", progress: 98 },
  { id: "report", label: "Generate Report", progress: 100 },
];

async function timed(fn) {
  const t0 = performance.now();
  const value = await fn();
  return { ms: Math.round(performance.now() - t0), value };
}

function classifyError(e, runtime, { forWrite = false } = {}) {
  const authenticated = Boolean(runtime?.authenticated);
  const connectionOnly = runtime?.executionMode === HEALTH_CHECK_MODE_CONNECTION_ONLY;
  const firestore = classifyFirestoreTestError(e, {
    authenticated,
    connectionOnly: connectionOnly && !forWrite,
    forWrite,
  });
  if (firestore.kind === "rules_responded") {
    return { warning: "rules_responded", label: firestore.userMessage };
  }
  if (firestore.kind === "write_skipped") {
    return { warning: "skipped", label: firestore.userMessage };
  }
  if (firestore.kind === "unauthenticated") {
    return { warning: "unauthenticated", label: firestore.userMessage };
  }
  if (firestore.kind === "permission_denied") {
    return { warning: "permission_denied", label: firestore.userMessage };
  }
  if (firestore.kind === "invalid_argument") {
    return { warning: "invalid_collection", label: firestore.userMessage };
  }

  const code = e?.code || "";
  const message = String(e?.message || "");
  if (code === "resource-exhausted") return { warning: "quota_exceeded", label: "Quota exceeded" };
  if (code === "unavailable") return { warning: "offline", label: "Firestore unavailable / offline" };
  if (code === "failed-precondition") return { warning: "missing_indexes", label: "Missing Firestore index" };
  if (message.includes("timed out") || code === "deadline-exceeded") {
    return { warning: "network_timeout", label: "Network timeout" };
  }
  return { warning: "error", label: message || code || "Unknown error" };
}

/**
 * Runs the full 17-step Firestore Database Test Suite against a connection.
 * Uses the shared Studio connection runtime (same cache as Health Check) and only
 * creates temporary documents inside the studio_runtime_test sandbox.
 *
 * @param {string|object} connectionOrId  connection id or connection object
 * @param {object} options  { verifiedBy, onProgress }
 */
export async function runDeepDatabaseTest(connectionOrId, options = {}) {
  const { onProgress, verifiedBy = "" } = options;
  const startedAt = nowIso();
  const startedPerf = performance.now();

  const conn = await resolveConnectionRuntimeConfig(connectionOrId);

  const steps = [];
  const warnings = [];
  const latency = {};
  const permissions = {
    read: { status: "unknown", message: "" },
    write: { status: "unknown", message: "" },
    update: { status: "unknown", message: "" },
    delete: { status: "unknown", message: "" },
    batch: { status: "unknown", message: "" },
    transaction: { status: "unknown", message: "" },
  };
  const createdDocIds = [];
  let collections = null;
  let storageResult = null;

  const stepDef = (id) => DEEP_TEST_STEPS.find((s) => s.id === id) || { label: id, progress: 0 };

  const emit = (extra = {}) => {
    if (typeof onProgress !== "function") return;
    const last = steps[steps.length - 1];
    onProgress({
      progress: extra.progress ?? last?.progress ?? 0,
      label: extra.label ?? last?.label ?? "",
      phase: extra.phase || "running",
      steps: steps.map((s) => ({ ...s })),
      ...extra,
    });
  };

  const beginStep = (id) => {
    const def = stepDef(id);
    emit({ progress: def.progress, label: def.label, phase: "step", stepId: id });
  };

  const pushStep = (id, status, message, latencyMs = 0, details = null) => {
    const def = stepDef(id);
    const entry = { id, label: def.label, progress: def.progress, status, message, latencyMs, details };
    steps.push(entry);
    emit({ progress: def.progress, label: def.label, phase: "stepDone", stepId: id });
    return entry;
  };

  const addWarning = (type, severity, message) => {
    if (warnings.some((w) => w.type === type && w.message === message)) return;
    warnings.push({ type, severity, message });
  };

  const liveCapable = hasLiveCredentials(conn);

  let runtime = null;
  let fs = null;
  let authenticated = false;
  const suite = "deep_test";
  const logOp = (label, value) => logFirestoreOperationResult(suite, label, value);

  try {
    // ---- STEP 1: Configuration Validation ------------------------------------
    beginStep("configuration");
    const configValidation = validateConfiguration(conn || {});
    const configFields = configValidation.result.fields;
    if (!conn) {
      pushStep("configuration", "failed", "Connection not found");
    } else if (!configValidation.allRequiredOk) {
      const missing = configValidation.issues
        .filter((i) => i.severity === "error")
        .map((i) => i.field)
        .join(", ");
      pushStep("configuration", "failed", `Missing required fields: ${missing}`, 0, { fields: configFields });
      addWarning("configuration", "error", `Missing required Firebase config: ${missing}`);
    } else {
      pushStep(
        "configuration",
        "success",
        "All Firebase configuration fields validated",
        0,
        { fields: configFields }
      );
    }

    const configOk = Boolean(conn) && configValidation.allRequiredOk;

    if (!configOk || !liveCapable) {
      if (configOk && !liveCapable) {
        addWarning("masked", "warning", "API key not available — live database tests skipped (configuration only).");
      }
      // Fill remaining steps as skipped so the report is complete.
      ["authentication", "storage", "read", "write", "readback", "update", "transaction", "batchWrite", "batchDelete", "delete", "rollback"].forEach((id) => {
        pushStep(id, "skipped", !liveCapable ? "Skipped — API key unavailable" : "Skipped — configuration invalid");
      });
      const report = finalize({
        conn, steps, warnings, latency, permissions, collections,
        startedAt, startedPerf, verifiedBy, configFields, liveTested: false,
      });
      emit({ progress: 100, label: "Generate Report", phase: "done", report });
      persistHistory(conn, report, verifiedBy);
      return report;
    }

    runtime = await resolveConnectionTestRuntime(conn.id, { suite: "deep_test" });
    latency.init = Math.round(performance.now() - startedPerf);
    fs = runtime.firestore;
    authenticated = runtime.authenticated;
    const testDocRef = doc(fs, DEEP_TEST_COLLECTION, DEEP_TEST_DOC_ID);

    // ---- STEP 2: Authentication Test -----------------------------------------
    beginStep("authentication");
    try {
      const authResult = await checkAuthentication(runtime);
      latency.authentication = authResult.latencyMs || 0;
      const authStatus = authResult.status === "failed"
        ? "failed"
        : authResult.status === "not_signed_in"
          ? "success"
          : "success";
      pushStep("authentication", authStatus, authResult.message, authResult.latencyMs);
      if (authResult.status === "failed") addWarning("authentication", "error", authResult.message);
    } catch (e) {
      const c = classifyError(e, runtime);
      pushStep("authentication", "failed", c.label);
      addWarning(c.warning, "error", `Authentication: ${c.label}`);
    }

    // ---- STEP 2b: Storage Test (same engine as Verify Connection) --------------
    beginStep("storage");
    try {
      storageResult = await checkStorage(runtime, conn);
      latency.storage = storageResult.latencyMs || 0;
      const storageStatus = storageResult.status === "failed" ? "failed"
        : storageResult.status === "not_configured" ? "skipped"
          : storageResult.status === "warning" ? "warning" : "success";
      pushStep("storage", storageStatus, storageResult.message, storageResult.latencyMs || 0, { storage: storageResult });
      if (storageResult.status === "failed") {
        addWarning("storage", "warning", storageResult.message);
      }
    } catch (e) {
      const c = classifyError(e, runtime);
      pushStep("storage", "failed", c.label);
      addWarning(c.warning, "warning", `Storage: ${c.label}`);
    }

    // ---- STEP 3: Firestore Read Test -----------------------------------------
    beginStep("read");
    try {
      const { ms, value: snap } = await timed(() => getDocs(query(collection(fs, "studio_health"), limit(1))));
      latency.read = ms;
      permissions.read = { status: "allowed", message: "Read permitted" };
      if (snap.empty) {
        // studio_health missing — create a temporary probe doc to verify read path.
        const probeId = `probe_${Date.now()}`;
        const probeRef = doc(fs, DEEP_TEST_COLLECTION, probeId);
        try {
          await setDoc(probeRef, { _probe: true, createdAt: nowIso() });
          createdDocIds.push(probeId);
          const probeSnap = await getDoc(probeRef);
          pushStep(
            "read",
            "success",
            `studio_health empty — verified read via temporary ${DEEP_TEST_COLLECTION} collection (exists: ${probeSnap.exists()})`,
            ms
          );
        } catch (probeErr) {
          const c = classifyError(probeErr, runtime);
          pushStep("read", "success", `studio_health reachable but empty (temporary probe blocked: ${c.label})`, ms);
        }
      } else {
        pushStep("read", "success", `Read studio_health (${snap.size} doc sampled)`, ms);
      }
      logOp("Read Result", permissions.read.message);
    } catch (e) {
      const c = classifyError(e, runtime);
      latency.read = 0;
      if (c.warning === "rules_responded") {
        permissions.read = { status: "allowed", message: c.label };
        pushStep("read", "success", c.label);
        logOp("Read Result", c.label);
      } else {
        permissions.read = { status: c.warning === "permission_denied" ? "denied" : "failed", message: c.label };
        pushStep("read", c.warning === "permission_denied" ? "denied" : "failed", c.label);
        addWarning(c.warning, c.warning === "permission_denied" ? "warning" : "error", `Read: ${c.label}`);
        logOp("Read Result", c.label);
      }
    }

    // ---- STEP 4: Firestore Write Test ----------------------------------------
    beginStep("write");
    let connectionTestCreated = false;
    try {
      const payload = {
        timestamp: nowIso(),
        version: FIREBASE_SDK_VERSION,
        createdBy: verifiedBy || "studio-deep-test",
        status: "testing",
      };
      const { ms } = await timed(() => setDoc(testDocRef, payload));
      latency.write = ms;
      connectionTestCreated = true;
      createdDocIds.push(DEEP_TEST_DOC_ID);
      permissions.write = { status: "allowed", message: "Write permitted" };
      pushStep("write", "success", `Wrote ${DEEP_TEST_COLLECTION}/${DEEP_TEST_DOC_ID} (4 fields)`, ms);
      logOp("Write Result", "success");
    } catch (e) {
      const c = classifyError(e, runtime, { forWrite: true });
      latency.write = 0;
      if (c.warning === "skipped") {
        permissions.write = { status: "skipped", message: c.label };
        pushStep("write", "skipped", c.label);
      } else {
        permissions.write = { status: c.warning === "permission_denied" ? "denied" : "failed", message: c.label };
        pushStep("write", c.warning === "permission_denied" ? "read_only" : "failed", c.label);
        addWarning(c.warning, "warning", `Write: ${c.label}`);
      }
      logOp("Write Result", c.label);
    }

    // ---- STEP 5: Firestore Read Back -----------------------------------------
    beginStep("readback");
    if (connectionTestCreated) {
      try {
        const { ms, value: snap } = await timed(() => getDoc(testDocRef));
        const data = snap.exists() ? snap.data() : {};
        const fieldCount = Object.keys(data).length;
        const hasTimestamp = Boolean(data.timestamp);
        pushStep(
          "readback",
          snap.exists() && fieldCount === 4 && hasTimestamp ? "success" : "warning",
          `Document exists: ${snap.exists()} · fields: ${fieldCount} · timestamp: ${hasTimestamp ? "present" : "missing"}`,
          ms,
          { fieldCount, hasTimestamp, timestamp: data.timestamp || null }
        );
      } catch (e) {
        const c = classifyError(e, runtime);
        pushStep("readback", "failed", c.label);
        addWarning(c.warning, "error", `Read back: ${c.label}`);
      }
    } else {
      pushStep("readback", "skipped", "Skipped — write test did not create the document");
    }

    // ---- STEP 6: Update Test -------------------------------------------------
    beginStep("update");
    if (connectionTestCreated) {
      try {
        const { ms } = await timed(() => updateDoc(testDocRef, { status: "verified", verified: true }));
        latency.update = ms;
        permissions.update = { status: "allowed", message: "Update permitted" };
        pushStep("update", "success", "Updated status=verified · verified=true", ms);
        logOp("Update Result", "success");
      } catch (e) {
        const c = classifyError(e, runtime, { forWrite: true });
        latency.update = 0;
        if (c.warning === "skipped") {
          permissions.update = { status: "skipped", message: c.label };
          pushStep("update", "skipped", c.label);
        } else {
          permissions.update = { status: c.warning === "permission_denied" ? "denied" : "failed", message: c.label };
          pushStep("update", c.warning === "permission_denied" ? "read_only" : "failed", c.label);
          addWarning(c.warning, "warning", `Update: ${c.label}`);
        }
        logOp("Update Result", c.label);
      }
    } else {
      permissions.update = { status: "skipped", message: "Skipped — no document to update" };
      pushStep("update", "skipped", "Skipped — write test did not create the document");
      logOp("Update Result", "skipped");
    }

    // ---- STEP 7: Transaction Test --------------------------------------------
    beginStep("transaction");
    if (connectionTestCreated) {
      try {
        const { ms } = await timed(() =>
          runTransaction(fs, async (tx) => {
            const snap = await tx.get(testDocRef);
            const current = snap.exists() ? snap.data() : {};
            tx.set(testDocRef, { ...current, txCount: (current.txCount || 0) + 1, txAt: nowIso() }, { merge: true });
          })
        );
        latency.transaction = ms;
        permissions.transaction = { status: "allowed", message: "Transaction commit permitted" };
        pushStep("transaction", "success", "Transaction committed successfully", ms);
        logOp("Transaction Result", "success");
      } catch (e) {
        const c = classifyError(e, runtime, { forWrite: true });
        latency.transaction = 0;
        if (c.warning === "skipped") {
          permissions.transaction = { status: "skipped", message: c.label };
          pushStep("transaction", "skipped", c.label);
        } else {
          permissions.transaction = { status: c.warning === "permission_denied" ? "denied" : "failed", message: c.label };
          pushStep("transaction", c.warning === "permission_denied" ? "denied" : "failed", c.label);
          addWarning(c.warning, "warning", `Transaction: ${c.label}`);
        }
        logOp("Transaction Result", c.label);
      }
    } else {
      permissions.transaction = { status: "skipped", message: "Skipped — no document to transact" };
      pushStep("transaction", "skipped", "Skipped — write test did not create the document");
      logOp("Transaction Result", "skipped");
    }

    // ---- STEP 8: Batch Write Test --------------------------------------------
    beginStep("batchWrite");
    const batchIds = [];
    if (permissions.write.status === "allowed") {
      try {
        const { ms } = await timed(async () => {
          const batch = writeBatch(fs);
          for (let i = 0; i < DEEP_TEST_BATCH_SIZE; i += 1) {
            const id = `batch_${Date.now()}_${i}`;
            batch.set(doc(fs, DEEP_TEST_COLLECTION, id), { _batch: true, index: i, createdAt: nowIso() });
            batchIds.push(id);
          }
          await batch.commit();
        });
        latency.batch = ms;
        createdDocIds.push(...batchIds);
        // Verify all committed.
        let committed = 0;
        for (const id of batchIds) {
          // eslint-disable-next-line no-await-in-loop
          const s = await getDoc(doc(fs, DEEP_TEST_COLLECTION, id));
          if (s.exists()) committed += 1;
        }
        permissions.batch = { status: "allowed", message: "Batch write permitted" };
        pushStep(
          "batchWrite",
          committed === DEEP_TEST_BATCH_SIZE ? "success" : "warning",
          `${committed}/${DEEP_TEST_BATCH_SIZE} batch documents committed`,
          ms,
          { committed }
        );
        logOp("Batch Result", `${committed}/${DEEP_TEST_BATCH_SIZE} committed`);
      } catch (e) {
        const c = classifyError(e, runtime, { forWrite: true });
        latency.batch = 0;
        if (c.warning === "skipped") {
          permissions.batch = { status: "skipped", message: c.label };
          pushStep("batchWrite", "skipped", c.label);
        } else {
          permissions.batch = { status: c.warning === "permission_denied" ? "denied" : "failed", message: c.label };
          pushStep("batchWrite", c.warning === "permission_denied" ? "denied" : "failed", c.label);
          addWarning(c.warning, "warning", `Batch write: ${c.label}`);
        }
        logOp("Batch Result", c.label);
      }
    } else {
      permissions.batch = { status: "skipped", message: "Skipped — write access unavailable" };
      pushStep("batchWrite", "skipped", "Skipped — write access unavailable");
      logOp("Batch Result", "skipped");
    }

    // ---- STEP 9: Batch Delete Test -------------------------------------------
    beginStep("batchDelete");
    if (batchIds.length) {
      try {
        const { ms } = await timed(async () => {
          const batch = writeBatch(fs);
          batchIds.forEach((id) => batch.delete(doc(fs, DEEP_TEST_COLLECTION, id)));
          await batch.commit();
        });
        latency.batchDelete = ms;
        // Remove from cleanup tracking.
        batchIds.forEach((id) => {
          const idx = createdDocIds.indexOf(id);
          if (idx >= 0) createdDocIds.splice(idx, 1);
        });
        pushStep("batchDelete", "success", `Deleted ${batchIds.length} batch documents`, ms);
      } catch (e) {
        const c = classifyError(e, runtime);
        pushStep("batchDelete", "failed", c.label);
        addWarning(c.warning, "warning", `Batch delete: ${c.label}`);
      }
    } else {
      pushStep("batchDelete", "skipped", "Skipped — no batch documents to delete");
    }

    // ---- STEP 10: Delete Test ------------------------------------------------
    beginStep("delete");
    if (connectionTestCreated) {
      try {
        const { ms } = await timed(() => deleteDoc(testDocRef));
        latency.delete = ms;
        const check = await getDoc(testDocRef);
        const removed = !check.exists();
        if (removed) {
          const idx = createdDocIds.indexOf(DEEP_TEST_DOC_ID);
          if (idx >= 0) createdDocIds.splice(idx, 1);
        }
        permissions.delete = { status: "allowed", message: "Delete permitted" };
        pushStep("delete", removed ? "success" : "warning", `Document removed: ${removed}`, ms, { removed });
        logOp("Delete Result", removed ? "success" : "warning");
      } catch (e) {
        const c = classifyError(e, runtime, { forWrite: true });
        latency.delete = 0;
        if (c.warning === "skipped") {
          permissions.delete = { status: "skipped", message: c.label };
          pushStep("delete", "skipped", c.label);
        } else {
          permissions.delete = { status: c.warning === "permission_denied" ? "denied" : "failed", message: c.label };
          pushStep("delete", c.warning === "permission_denied" ? "read_only" : "failed", c.label);
          addWarning(c.warning, "warning", `Delete: ${c.label}`);
        }
        logOp("Delete Result", c.label);
      }
    } else {
      permissions.delete = { status: "skipped", message: "Skipped — no document to delete" };
      pushStep("delete", "skipped", "Skipped — write test did not create the document");
    }

    // ---- STEP 11: Rollback Verification --------------------------------------
    beginStep("rollback");
    try {
      // Best-effort cleanup of anything still tracked, then verify sandbox is clean.
      const pending = [...createdDocIds];
      const removedCount = await cleanupHealthTestDocuments(fs, pending);
      createdDocIds.length = 0;
      const remaining = Math.max(0, pending.length - removedCount);
      pushStep(
        "rollback",
        remaining === 0 ? "success" : "warning",
        remaining === 0
          ? "Database returned to original state — no temporary documents remain"
          : `${remaining} temporary document(s) could not be removed`,
        0,
        { remaining }
      );
      if (remaining > 0) addWarning("rollback", "warning", `${remaining} temporary document(s) remain in ${DEEP_TEST_COLLECTION}`);
    } catch (e) {
      const c = classifyError(e, runtime);
      pushStep("rollback", "warning", c.label);
    }

    // ---- STEP 12: Permission Test --------------------------------------------
    beginStep("permissions");
    const permSummary = summarizePermissions(permissions);
    pushStep("permissions", permSummary.status, permSummary.message, 0, { permissions });

    // ---- STEP 13: Collection Discovery ---------------------------------------
    beginStep("collections");
    try {
      collections = await runCollectionTests(runtime);
      pushStep(
        "collections",
        "success",
        `${collections.totalAccessible} collections accessible · largest: ${collections.largestCollection} (${collections.largestCount})`,
        0,
        { collections }
      );
    } catch (e) {
      const c = classifyError(e, runtime);
      pushStep("collections", "warning", c.label);
    }

    // ---- STEP 14: Database Information ----------------------------------------
    beginStep("database");
    const database = {
      name: "(default)",
      mode: "Native",
      region: conn.region || "(default)",
      sdkVersion: FIREBASE_SDK_VERSION,
      projectId: conn.firebaseProjectId || "",
      storageBucket: conn.storageBucket || "—",
    };
    pushStep("database", "success", `Native mode · project ${database.projectId} · SDK ${database.sdkVersion}`, 0, { database });

    // ---- STEP 15: Performance ------------------------------------------------
    beginStep("performance");
    const performanceReport = buildPerformance(latency);
    pushStep(
      "performance",
      "success",
      `avg ${performanceReport.average} ms · min ${performanceReport.minimum} ms · max ${performanceReport.maximum} ms`,
      0,
      { performance: performanceReport }
    );

    // ---- STEP 16: Warnings ---------------------------------------------------
    beginStep("warnings");
    detectEnvironmentWarnings(addWarning, collections);
    pushStep(
      "warnings",
      warnings.some((w) => w.severity === "error") ? "warning" : "success",
      warnings.length ? `${warnings.length} warning(s) detected` : "No warnings detected",
      0,
      { warnings: [...warnings] }
    );

    // ---- STEP 17: Generate Report --------------------------------------------
    beginStep("report");
    const report = finalize({
      conn, steps, warnings, latency, permissions, collections,
      startedAt, startedPerf, verifiedBy, configFields, database,
      performance: performanceReport, liveTested: true, storageResult,
    });
    pushStep("report", "success", `Health ${report.healthScore}% · ${report.overall}`, 0, null);
    emit({ progress: 100, label: "Generate Report", phase: "done", report });

    persistHistory(conn, report, verifiedBy);
    return report;
  } catch (e) {
    const c = classifyError(e, runtime);
    addWarning(c.warning, "error", c.label);
    const report = finalize({
      conn, steps, warnings, latency, permissions, collections,
      startedAt, startedPerf, verifiedBy, configFields: {}, liveTested: false,
    });
    report.overall = "failed";
    emit({ progress: 100, label: "Generate Report", phase: "done", report });
    persistHistory(conn, report, verifiedBy);
    return report;
  } finally {
    // Safety net: remove any temporary document that slipped through.
    if (fs && createdDocIds.length) {
      await cleanupHealthTestDocuments(fs, createdDocIds, DEEP_TEST_COLLECTION);
    }
  }
}

function summarizePermissions(permissions) {
  const allowed = Object.values(permissions).filter((p) => p.status === "allowed").length;
  const denied = Object.values(permissions).filter((p) => p.status === "denied").length;
  const skipped = Object.values(permissions).filter((p) => p.status === "skipped").length;
  const readAllowed = permissions.read.status === "allowed";
  const writeAllowed = permissions.write.status === "allowed";
  const writeSkipped = permissions.write.status === "skipped";

  if (readAllowed && writeSkipped) {
    return {
      status: "success",
      message: `Read verified · ${WRITE_TESTS_SKIPPED_MESSAGE}`,
    };
  }
  if (readAllowed && !writeAllowed && denied > 0) {
    return { status: "read_only", message: "Read-only access — write/update/delete denied by security rules" };
  }
  if (allowed === 6) return { status: "success", message: "Full access — all operations permitted" };
  if (allowed === 0 && skipped === 0) return { status: "failed", message: "No operations permitted" };
  if (allowed === 0 && skipped > 0 && readAllowed) {
    return { status: "warning", message: `Read verified · ${skipped} operation(s) skipped` };
  }
  return { status: "warning", message: `${allowed}/6 operations permitted, ${denied} denied, ${skipped} skipped` };
}

function buildPerformance(latency) {
  const keys = ["authentication", "storage", "read", "write", "update", "delete", "batch", "transaction"];
  const samples = keys.map((k) => latency[k]).filter((v) => typeof v === "number" && v > 0);
  const average = samples.length ? Math.round(samples.reduce((a, b) => a + b, 0) / samples.length) : 0;
  return {
    init: latency.init || 0,
    authentication: latency.authentication || 0,
    storage: latency.storage || 0,
    read: latency.read || 0,
    write: latency.write || 0,
    update: latency.update || 0,
    delete: latency.delete || 0,
    batch: latency.batch || 0,
    transaction: latency.transaction || 0,
    average,
    maximum: samples.length ? Math.max(...samples) : 0,
    minimum: samples.length ? Math.min(...samples) : 0,
  };
}

function detectEnvironmentWarnings(addWarning, collections) {
  if (typeof navigator !== "undefined" && !navigator.onLine) {
    addWarning("offline", "error", "Browser reports offline");
  }
  if (collections && collections.totalAccessible === 0) {
    addWarning("firestore_disabled", "warning", "No collections were accessible — Firestore may be disabled or rules deny reads");
  }
}

function scorePermissions(permissions) {
  const relevant = Object.values(permissions).filter((p) => p.status !== "skipped" && p.status !== "unknown");
  if (!relevant.length) return 0;
  const allowed = relevant.filter((p) => p.status === "allowed").length;
  return Math.round((allowed / relevant.length) * 100);
}

function scorePerformance(perf) {
  const avg = perf.average || 0;
  if (avg === 0) return 0;
  if (avg < 150) return 100;
  if (avg < 300) return 92;
  if (avg < 600) return 80;
  if (avg < 1000) return 65;
  if (avg < 2000) return 45;
  return 25;
}

function scoreSecurity(warnings, permissions) {
  let score = 100;
  warnings.forEach((w) => {
    if (w.severity === "error") score -= 15;
    else score -= 5;
  });
  // Write allowed on the sandbox is expected; no penalty. Denials indicate stricter rules.
  if (permissions.write.status === "denied") score = Math.min(100, score + 5);
  return Math.max(0, Math.min(100, score));
}

function scoreHealth(steps, permissions) {
  const core = steps.filter((s) => ["configuration", "authentication", "read"].includes(s.id));
  if (core.some((s) => s.status === "failed")) return Math.min(50, baseHealth(steps));
  return baseHealth(steps, permissions);
}

function baseHealth(steps, permissions) {
  let score = 100;
  steps.forEach((s) => {
    if (s.status === "failed") score -= 12;
    else if (s.status === "warning") score -= 4;
  });
  if (permissions && permissions.read.status === "denied") score -= 15;
  return Math.max(0, Math.min(100, score));
}

function finalize({
  conn, steps, warnings, latency, permissions, collections,
  startedAt, startedPerf, verifiedBy, configFields, database, performance: perf, liveTested, storageResult,
}) {
  const completedAt = nowIso();
  const duration = Math.round(performance.now() - startedPerf);
  const performanceReport = perf || buildPerformance(latency);

  const healthScore = scoreHealth(steps, permissions);
  const performanceScore = liveTested ? scorePerformance(performanceReport) : 0;
  const permissionScore = liveTested ? scorePermissions(permissions) : 0;
  const securityScore = scoreSecurity(warnings, permissions);

  const hasFailed = steps.some((s) => s.status === "failed");
  const hasWarning = steps.some((s) => s.status === "warning") || warnings.length > 0;
  const overall = hasFailed ? "failed" : hasWarning ? "warning" : "passed";

  const recommendation = buildRecommendation(overall, permissions, performanceReport, warnings);

  const dbInfo = database || {
    name: "(default)",
    mode: "Native",
    region: conn?.region || "(default)",
    sdkVersion: FIREBASE_SDK_VERSION,
    projectId: conn?.firebaseProjectId || "",
    storageBucket: conn?.storageBucket || "—",
  };

  const report = {
    engine: "firestore-deep-test-suite",
    connectionId: conn?.id || "",
    connectionName: conn?.name || "",
    projectId: conn?.firebaseProjectId || "",
    startedAt,
    completedAt,
    checkedAt: completedAt,
    duration,
    overall,
    healthScore,
    performanceScore,
    permissionScore,
    securityScore,
    steps,
    permissions,
    performance: performanceReport,
    collections,
    database: dbInfo,
    warnings,
    recommendation,
    advanced: {
      browser: detectBrowser(),
      os: detectOs(),
      sdkVersion: FIREBASE_SDK_VERSION,
      verifiedBy: verifiedBy || "—",
      configFields: configFields || {},
      storage: storageResult || null,
    },
  };

  report.raw = JSON.parse(JSON.stringify(report));
  return report;
}

function buildRecommendation(overall, permissions, perf, warnings) {
  if (overall === "failed") {
    return "One or more critical tests failed. Verify the Firebase configuration, project ID and Firestore security rules, then retry.";
  }
  if (permissions.read.status === "allowed" && permissions.write.status === "denied") {
    return "Connection is read-only. Grant write access in Firestore security rules if this connection should support writes.";
  }
  if ((perf.average || 0) > 1000) {
    return "Database is reachable but latency is high. Check the client network and consider a closer Firestore region.";
  }
  if (warnings.length) {
    return "All operations succeeded with minor warnings. Review the Warnings tab for details.";
  }
  return "All Firestore operations completed successfully. The connection is healthy and fully operational.";
}

function persistHistory(conn, report, verifiedBy) {
  if (!conn?.id) return;
  saveConnectionDeepTestHistory({
    connectionId: conn.id,
    projectId: conn.firebaseProjectId || "",
    startedAt: report.startedAt,
    completedAt: report.completedAt,
    duration: report.duration,
    healthScore: report.healthScore,
    performanceScore: report.performanceScore,
    permissionScore: report.permissionScore,
    securityScore: report.securityScore,
    browser: report.advanced.browser,
    os: report.advanced.os,
    sdkVersion: report.advanced.sdkVersion,
    verifiedBy: verifiedBy || "",
    status: report.overall,
    report,
  }).catch(() => {});
}
